import { directionLabel, movementRows, recentMonths } from './movement';
import type { InventoryItem } from './queries';
import type { StockPoint } from './burnRate';
import type { MovementRow } from './movement';

/**
 * Stock across the last six months.
 *
 * ── What this answers that the table above cannot ──────────────────────────
 *
 * The panel above is one photograph: what is on the shelf as of the newest
 * report. Every question worth asking is about the sequence — is this falling
 * faster than last quarter, did the January restock actually land, is the
 * thing at 40 days heading for zero or has it been flat since March. The
 * history was being collected from the first upload and used for burn rates,
 * and none of it was readable.
 *
 * ── Why a table and not a chart ────────────────────────────────────────────
 *
 * Fifty-six materials is far too many lines for one chart and far too many
 * charts for one page. A table sorted by the steepest fall puts the material
 * you should be reordering at the top, which is the decision this screen
 * exists to support. A chart would look more impressive and answer less.
 */

const MONTHS = 6;

export function InventoryHistory({
  history,
  items,
}: {
  history: Map<string, StockPoint[]> | undefined;
  /** The latest snapshot, only for the material descriptions. */
  items: InventoryItem[];
}) {
  if (history === undefined) {
    return null;
  }

  const months = recentMonths(history, MONTHS);
  if (months.length < 2) {
    // One report is not a trend, and drawing a single column implies it might
    // be. Said in the same words the burn-rate line uses, because it is the
    // same limitation.
    return (
      <p className="order-page-muted">
        Only one month of reports so far. A trend appears once a second report is uploaded.
      </p>
    );
  }

  const descriptions = new Map(items.map((item) => [item.sku, item.description]));
  const categories = new Map(items.map((item) => [item.sku, item.category]));
  const rows = movementRows(history, descriptions, months);

  return (
    <div className="inventory-history">
      <table className="ui-table">
        <thead>
          <tr>
            <th scope="col">SKU</th>
            <th scope="col">Material</th>
            {months.map((month) => (
              <th key={month} scope="col" className="inventory-history-month">
                {monthLabel(month)}
              </th>
            ))}
            <th scope="col">Over {months.length} months</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.sku}>
              <th scope="row">{row.sku}</th>
              <td>{row.description}</td>
              {row.quantities.map((quantity, index) => (
                <td key={months[index]} className="inventory-history-month">
                  {/* An em dash for "not counted", a real 0 for "none left".
                      Printing a gap as zero would show a cliff that never
                      happened — see movement.ts. */}
                  {quantity === null ? (
                    <span className="deal-note-empty" title="Not in this month's report">
                      —
                    </span>
                  ) : (
                    formatQty(quantity, categories.get(row.sku) ?? '')
                  )}
                </td>
              ))}
              <td>
                <Change row={row} category={categories.get(row.sku) ?? ''} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="order-page-muted">
        A rise means a delivery landed, not that consumption stopped — which is also why those
        months are left out of the burn rate.
      </p>
    </div>
  );
}

/**
 * The change, with its direction stated in words as well as sign.
 *
 * "Used" and "Restocked" rather than an arrow alone: down is not bad news and
 * up is not good news here, and an unlabelled red arrow would be read as a
 * problem when it is just a factory using the materials it was sent.
 */
function Change({ row, category }: { row: MovementRow; category: string }) {
  if (row.change === null) {
    return <span className="deal-note-empty">{directionLabel(row.direction)}</span>;
  }
  return (
    <span
      className={
        row.direction === 'down'
          ? 'inventory-change inventory-change--down'
          : row.direction === 'up'
            ? 'inventory-change inventory-change--up'
            : 'inventory-change'
      }
    >
      {row.change > 0 ? '+' : ''}
      {formatQty(row.change, category)}
      <span className="inventory-change-word">{directionLabel(row.direction)}</span>
    </span>
  );
}

/** Spools come in fractions; rounding 15.87 to 16 would overstate what is left. */
function formatQty(value: number, category: string): string {
  return category === 'spool' ? value.toFixed(2) : value.toLocaleString();
}

/** "Mar 2026" — the month is what people say, the day is noise in a header. */
function monthLabel(reportDate: string): string {
  const parsed = new Date(`${reportDate}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) {
    return reportDate;
  }
  return parsed.toLocaleDateString(undefined, {
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
}
